import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  theme,
} from "antd";
import {
  CloseOutlined,
  ExperimentOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ApiService } from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { fieldsUsedBy } from "./validation";
import { isConditional } from "./conditionText";
import type { ProcessSection, ProcessWorkflow } from "../../api";
import type { FieldRef, StageDraft } from "./types";

const { Text } = Typography;

type Answer = { field: string; values: string[] };

type Props = {
  templateId: number;
  fields: FieldRef[];
  stages: StageDraft[];
  /** Sections and sign-offs as the draft holds them. Their conditions decide
   *  which questions are asked and who signs, so a run reporting only the steps
   *  would say "Finance approval runs" and leave out that on this case it runs
   *  with two of its three names on it. */
  sections: ProcessSection[];
  workflows: ProcessWorkflow[];
  onResult: (applies: Record<string, boolean> | null) => void;
  onClose: () => void;
};

/**
 * Answer questions and watch the canvas rearrange itself.
 *
 * "Try it" existed before, in a drawer, behind a button, with a Run button of
 * its own -- so the one feature that makes conditions comprehensible was three
 * clicks and a covered canvas away from the thing it explains. Conditions are
 * the easiest part of a process to get subtly wrong and the hardest to observe:
 * until this existed at all, the only way to find out what a rule did was to
 * raise a real case and watch.
 *
 * So it is a bar across the top of the canvas instead, and it re-runs as you
 * type. The canvas dims the steps that would be skipped and lights up the
 * bypass they would take, which turns "what does this rule do?" into something
 * you answer by looking rather than by reasoning.
 *
 * The rule engine stays on the server. Mirroring `conditions.evaluate` in
 * TypeScript would give two implementations of what decides a real case, and
 * the one that matters is the one in Python.
 */
const TryItBar = ({
  templateId,
  fields,
  stages,
  sections,
  workflows,
  onResult,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [skippedSections, setSkippedSections] = useState<string[]>([]);
  const [skippedSlots, setSkippedSlots] = useState<string[]>([]);
  // Who a derived slot ("the requester's manager") would actually ask, and the
  // sentence for the ones that resolve to nobody. The rule's *description* is
  // true whether or not this organisation chart can answer it, so the names are
  // the only way to find out before a real case gets stuck on the step.
  const [routed, setRouted] = useState<Array<{ label: string; who: string[] }>>([]);
  const [unresolved, setUnresolved] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const byName = useMemo(
    () => new Map(fields.map((f) => [f.name, f])),
    [fields]
  );

  /**
   * The questions the process actually branches on.
   *
   * Offering every field in the template is what the drawer did, and on a real
   * template that is a hundred-item dropdown of which four matter. Only fields
   * a rule mentions can change the answer, so those come first and the rest are
   * still reachable underneath.
   */
  const decidingFields = useMemo(() => {
    const rules = [
      ...stages.map((s) => s.condition),
      ...sections.map((s) => s.condition),
      ...workflows.flatMap((w) => w.roles.map((r) => r.condition)),
    ];
    const used = new Set(
      rules.flatMap((rule) => (isConditional(rule) ? fieldsUsedBy(rule) : []))
    );
    return fields.filter((f) => used.has(f.name));
  }, [stages, sections, workflows, fields]);

  const fieldOptions = useMemo(() => {
    const rest = fields.filter((f) => !decidingFields.includes(f));
    const toOption = (field: FieldRef) => ({
      value: field.name,
      label: `${field.label || field.name} · ${field.sectionTitle}`,
    });
    const groups = [];
    if (decidingFields.length > 0) {
      groups.push({
        label: t("Questions this process branches on"),
        options: decidingFields.map(toOption),
      });
    }
    if (rest.length > 0) {
      groups.push({ label: t("Everything else"), options: rest.map(toOption) });
    }
    return groups;
  }, [decidingFields, fields, t]);

  const run = useCallback(
    async (current: Answer[]) => {
      setRunning(true);
      setError("");
      try {
        const values: Record<string, unknown> = {};
        current.forEach((a) => {
          if (!a.field) return;
          // One tag reads as a plain answer, several as a list — matching how
          // the form itself stores a single choice versus a checkbox group.
          values[a.field] = a.values.length === 1 ? a.values[0] : a.values;
        });
        const data = await ApiService.apiMainTemplateProcessSimulateCreate(
          templateId,
          {
            values,
            // The layout on screen, not the saved one.
            stages: stages.map((s) => ({
              key: s.key,
              name: s.name,
              kind: s.kind,
              condition: s.condition,
            })),
            sections: sections.map((s) => ({
              id: s.id,
              title: s.title,
              condition: s.condition ?? {},
            })),
            slots: workflows.flatMap((w) =>
              w.roles.map((r) => ({
                id: r.id,
                title: r.title,
                workflow_name: w.name,
                condition: r.condition ?? {},
                // Sent so a slot switched to "the requester's manager" in the
                // draft is previewed before it is saved.
                assignment: r.assignment,
                resolver: r.resolver ?? {},
              }))
            ),
          }
        );
        onResult(
          Object.fromEntries((data.stages ?? []).map((s) => [s.key, s.applies]))
        );
        setSkipped(data.skipped ?? []);
        setSkippedSections(data.skipped_sections ?? []);
        setSkippedSlots(data.skipped_slots ?? []);
        setRouted(
          (data.slots ?? [])
            .filter((r) => r.applies && (r.who?.length ?? 0) > 0)
            .map((r) => ({ label: r.label, who: r.who ?? [] }))
        );
        setUnresolved(data.unresolved_slots ?? []);
      } catch (e: any) {
        setError(apiErrorMessage(e, t("Something went wrong")));
        onResult(null);
      }
      setRunning(false);
    },
    [templateId, stages, sections, workflows, onResult, t]
  );

  // Re-run as the answers change, and whenever the process itself is edited —
  // otherwise adding a rule leaves the canvas painted with a stale verdict,
  // which is worse than no verdict at all.
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(answers), 250);
    return () => clearTimeout(timer.current);
  }, [answers, run]);

  // Clear the painting when the bar closes; a dimmed step with no visible
  // reason for being dimmed reads as a bug.
  useEffect(() => () => onResult(null), [onResult]);

  const update = (index: number, patch: Partial<Answer>) =>
    setAnswers(answers.map((a, i) => (i === index ? { ...a, ...patch } : a)));

  const conditionalCount =
    stages.filter((s) => isConditional(s.condition)).length +
    sections.filter((s) => isConditional(s.condition)).length +
    workflows.reduce(
      (n, w) => n + w.roles.filter((r) => isConditional(r.condition)).length,
      0
    );

  return (
    <div
      style={{
        padding: "10px 12px",
        background: token.colorInfoBg,
        borderBottom: `1px solid ${token.colorInfoBorder}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <ExperimentOutlined style={{ color: token.colorInfoText }} />
        <Text strong style={{ fontSize: 13 }}>
          {t("Try it")}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
          {conditionalCount === 0
            ? t(
                "Nothing in this process has a rule yet, so every case walks all of it."
              )
            : t(
                "Answer as a case would: the canvas shows which steps it walks, and the line below which sections and signatures it needs."
              )}
        </Text>
        {running && (
          <ReloadOutlined spin style={{ color: token.colorTextTertiary }} />
        )}
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label={t("Close")}
        />
      </div>

      <Space wrap size={8} style={{ width: "100%" }}>
        {answers.map((answer, index) => {
          const options = byName.get(answer.field)?.options ?? [];
          return (
            <Space.Compact key={index}>
              <Select
                size="small"
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 200 }}
                placeholder={t("Pick a question")}
                value={answer.field || undefined}
                onChange={(v) => update(index, { field: v, values: [] })}
                options={fieldOptions}
              />
              {options.length > 0 ? (
                <Select
                  size="small"
                  mode="multiple"
                  allowClear
                  style={{ minWidth: 190 }}
                  placeholder={t("Pick the answer")}
                  value={answer.values}
                  onChange={(v) => update(index, { values: v })}
                  options={options.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              ) : (
                <Input
                  size="small"
                  style={{ width: 190 }}
                  placeholder={t("Type an answer")}
                  value={answer.values[0] ?? ""}
                  onChange={(e) =>
                    update(index, {
                      values: e.target.value ? [e.target.value] : [],
                    })
                  }
                />
              )}
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setAnswers(answers.filter((_, i) => i !== index))}
                aria-label={t("Remove this answer")}
              />
            </Space.Compact>
          );
        })}

        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setAnswers([...answers, { field: "", values: [] }])}
        >
          {t("Add an answer")}
        </Button>

        {answers.length > 0 && (
          <Button size="small" type="text" onClick={() => setAnswers([])}>
            {t("Clear")}
          </Button>
        )}
      </Space>

      <div style={{ marginTop: 8 }}>
        {error ? (
          <Text type="danger" style={{ fontSize: 12 }}>
            {error}
          </Text>
        ) : skipped.length + skippedSections.length + skippedSlots.length +
            routed.length + unresolved.length >
          0 ? (
          /* Three lines, not one merged list: a step that does not happen, a
             question that is not asked and a signature that is not collected
             are different facts, and one row of tags reads as three missing
             steps. */
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            {skipped.length > 0 && (
              <Space size={4} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("A case answering this way skips:")}
                </Text>
                {skipped.map((name) => (
                  <Tag key={name} style={{ marginInlineEnd: 0 }}>
                    {name || t("unnamed step")}
                  </Tag>
                ))}
              </Space>
            )}
            {skippedSections.length > 0 && (
              <Space size={4} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("It is never asked to fill in:")}
                </Text>
                {skippedSections.map((name) => (
                  <Tag key={name} style={{ marginInlineEnd: 0 }}>
                    {name || t("unnamed section")}
                  </Tag>
                ))}
              </Space>
            )}
            {skippedSlots.length > 0 && (
              <Space size={4} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("These signatures are not collected:")}
                </Text>
                {skippedSlots.map((name) => (
                  <Tag key={name} style={{ marginInlineEnd: 0 }}>
                    {name || t("unnamed slot")}
                  </Tag>
                ))}
              </Space>
            )}
            {/* Who a derived slot actually lands on. Resolved as if YOU had
                raised the case, since Try it has answers and no case — said
                out loud, because "Marat Manager" only makes sense once you
                know whose manager he is. */}
            {routed.length > 0 && (
              <Space size={4} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("If you raised it, these would be asked to sign:")}
                </Text>
                {routed.map((r) => (
                  <Tag key={r.label} color="blue" style={{ marginInlineEnd: 0 }}>
                    {r.label}: {r.who.join(", ")}
                  </Tag>
                ))}
              </Space>
            )}
            {/* The opposite fact from a skipped slot, and the one that matters:
                skipping is a decision the process made, an unresolved slot is a
                case that would stop on a step with no button on it. */}
            {unresolved.length > 0 && (
              <Space size={4} wrap>
                <Text type="danger" style={{ fontSize: 12 }}>
                  {t("Nobody could be found for:")}
                </Text>
                {unresolved.map((line) => (
                  <Tag key={line} color="error" style={{ marginInlineEnd: 0 }}>
                    {line}
                  </Tag>
                ))}
              </Space>
            )}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("A case answering this way walks every step.")}
          </Text>
        )}
      </div>
    </div>
  );
};

export default TryItBar;
